import { Paths } from "@app/routes";
import {
  Button,
  type CellContext,
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  type PaginationState,
  TabsContent,
  WithToolTip,
} from "@atlas/core";
import {
  FilterBuilder,
  formatIsoDateTimeDisplay,
  useTranslation,
} from "@atlas/sdk";
import type { IHubPackageDeployment } from "@models";
import { useSearchHubPackageDeployments, useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { getHubPackageDeploymentStatusBadge } from "../utils";
import { ExpandedCatalogs } from "./ExpandedCatalogs";

const PACKAGE_MANAGER_DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: false,
  height: "full",
  headerSticky: true,
} as const;

const PACKAGE_MANAGER_COLUMN_HEADER_CLASS =
  "text-foreground px-1 py-0.5 font-semibold text-sm";

export function DeploymentsTab() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyId = userInfo?.companyId ?? 0;

  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const filter = useMemo(() => {
    const builder = new FilterBuilder().equals("companyId", companyId);
    if (searchQuery.trim()) {
      builder.contains("name", searchQuery.trim());
    }
    return builder.build();
  }, [companyId, searchQuery]);

  const { data: deploymentsData, isLoading: deploymentsLoading } =
    useSearchHubPackageDeployments(
      {
        filter,
        mode: "DATA",
        startIndex: pagination.pageIndex * pagination.pageSize,
        limit: pagination.pageSize,
        sort: ["installedDatetime DESC"],
      },
      { enabled: companyId > 0 },
    );
  const deployments = useMemo(
    () =>
      deploymentsData?.pages.flatMap(
        (page: { items: IHubPackageDeployment[] }) => page.items,
      ) ?? [],
    [deploymentsData],
  );

  const totalCount = useMemo(
    () => deploymentsData?.pages[0]?.totalCount ?? 0,
    [deploymentsData],
  );

  const handleRowClick = useCallback(
    (row: IHubPackageDeployment) => {
      const pkgId = row.packageId;
      if (pkgId == null || pkgId <= 0) return;

      const deploymentId = row.deploymentId;
      const deploymentIdParam =
        deploymentId != null ? `deploymentId=${deploymentId}` : "";

      navigate(
        `/${Paths.PackageManager}?${deploymentIdParam}&fromTab=deployments`,
      );
    },
    [navigate],
  );

  const columns = useMemo<ColumnDef<IHubPackageDeployment, unknown>[]>(
    () => [
      {
        id: "expand",
        header: () => null,
        size: 28,
        minSize: 20,
        cell: ({ row }) => {
          return (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}>
              {row.getIsExpanded() ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          );
        },
        meta: {
          expandedContent: (deployment) => (
            <ExpandedCatalogs deployment={deployment} />
          ),
        },
      },
      {
        accessorKey: "name",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.package_manager.deployment_column_name")}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
      },
      {
        accessorKey: "targetSolutionLabel",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_subscribe.line_solution")}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const val = info.getValue() as string | undefined;
          return val ? (
            <div className="truncate max-w-[300px]" title={val}>
              {val}
            </div>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "packageLabel",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.package_manager.deployment_column_package")}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const val = info.getValue() as string | undefined;
          const pkgId = info.row.original.packageId;
          return val ? (
            <WithToolTip tooltipText={val}>
              <div className="truncate max-w-75">
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (pkgId) {
                      navigate(
                        `/${Paths.PackageManager}?packageId=${pkgId}&fromTab=packages`,
                      );
                    }
                  }}>
                  {val}
                </Button>
              </div>
            </WithToolTip>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "catalogs",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.sidebar.catalogs", {
              defaultValue: "Catalogs",
            })}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const row = info.row.original;
          return row.installedCatalogs?.length ?? 0;
        },
      },
      {
        accessorKey: "tenantName",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.package_manager.deployment_column_tenant")}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) =>
          (info.getValue() as string) ?? "—",
      },

      {
        accessorKey: "status",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const status = info.getValue() as string;
          return getHubPackageDeploymentStatusBadge(status) || "—";
        },
      },
      {
        accessorKey: "installedDatetime",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.package_manager.deployment_column_installed", {
              defaultValue: "Installed On",
            })}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const val = info.getValue() as string | undefined;
          return val ? formatIsoDateTimeDisplay(val) : "—";
        },
      },
      {
        accessorKey: "installedBy",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.package_manager.deployment_column_installed_by", {
              defaultValue: "Installed By",
            })}
            className={PACKAGE_MANAGER_COLUMN_HEADER_CLASS}
          />
        ),
        cell: (info: CellContext<IHubPackageDeployment, unknown>) => {
          const val = info.getValue() as string | undefined;
          return val ? (
            <div className="truncate max-w-50" title={val}>
              {val}
            </div>
          ) : (
            "—"
          );
        },
      },
    ],
    [t],
  );

  return (
    <TabsContent
      value="deployments"
      className="flex-1 min-h-0 h-full flex flex-col">
      <DataGrid<IHubPackageDeployment>
        data={deployments}
        columns={columns}
        recordCount={totalCount}
        tableLayout={PACKAGE_MANAGER_DATA_GRID_LAYOUT}
        paginationPlacement="toolbar"
        paginationConfig={{ showRowsPerPage: true }}
        manualPagination
        pageCount={Math.ceil(totalCount / pagination.pageSize)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={deploymentsLoading}
        loadingMessage={t("hub.package_manager.deployments_loading")}
        emptyMessage={t("hub.package_manager.no_deployments")}
        onRowClick={handleRowClick}
        className="flex-1 min-h-0 h-full"
        withExpandable
        withSearch={{
          searchKey: "name",
          placeholder: t("hub.package_manager.deployments_search_placeholder", {
            defaultValue: "Search by name",
          }),
          onChange: setSearchQuery,
        }}
      />
    </TabsContent>
  );
}
